import { motion } from "motion/react";
import { Star, Quote, TrendingUp, ShoppingCart, BarChart3 } from "lucide-react";

export function SocialProofSection() {
  const testimonials = [
    {
      name: "Carlos Mendes",
      role: "CEO, MegaStore Brasil",
      company: "R$ 2M+ em vendas/mês",
      avatar: "CM",
      rating: 5,
      text: "Recuperamos 38% dos carrinhos abandonados no primeiro mês. O ROI foi imediato. A Identity Resolution V32 transformou nossa operação.",
      metric: "+R$ 87k recuperados",
      color: "#10B981",
    },
    {
      name: "Fernanda Oliveira",
      role: "Head of Growth, TechCommerce",
      company: "500k+ visitas/mês",
      avatar: "FO",
      rating: 5,
      text: "Finalmente conseguimos conectar os pontos. Saber exatamente quem abandona o carrinho e por quê mudou completamente nossa estratégia de remarketing.",
      metric: "+45% conversão",
      color: "#00F5FF",
    },
    {
      name: "Rafael Santos",
      role: "CTO, Fashion Hub",
      company: "Marketplace multi-loja",
      avatar: "RS",
      rating: 5,
      text: "A performance é absurda. Zero impacto no tempo de carregamento e o Bot Detection economizou 30% do budget de ads eliminando tráfego falso.",
      metric: "0ms overhead",
      color: "#FF4D00",
    },
  ];

  const stats = [
    { value: "500+", label: "Lojas Ativas", icon: ShoppingCart },
    { value: "R$ 24M+", label: "Receita Recuperada", icon: TrendingUp },
    { value: "8.5M+", label: "Leads Identificados", icon: BarChart3 },
    { value: "98%", label: "Satisfação", icon: Star },
  ];

  return (
    <section className="relative py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0E14] via-[#0B0E14]/95 to-[#0B0E14]" />
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Star className="w-8 h-8 text-[#10B981] fill-current" />
              <span className="font-['Roboto_Mono'] text-[#10B981] text-sm tracking-[0.3em] uppercase">
                Prova Social
              </span>
            </div>
            <h2 className="font-['Sora'] text-4xl md:text-5xl font-bold text-white mb-6">
              Usado por quem{" "}
              <span className="text-[#10B981] drop-shadow-[0_0_30px_rgba(16,185,129,0.5)]">
                leva a sério
              </span>
              <br />
              seu e-commerce
            </h2>
            <p className="font-['Inter'] text-xl text-gray-300 max-w-2xl mx-auto">
              Lojas de alto volume que transformaram dados em receita real
            </p>
          </motion.div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0B0E14]/80 backdrop-blur-xl border border-[#10B981]/20 rounded-2xl p-6 text-center hover:border-[#10B981]/50 transition-all"
                style={{
                  boxShadow: "0 0 30px rgba(16,185,129,0.1)",
                }}
              >
                <stat.icon className="w-8 h-8 text-[#10B981] mx-auto mb-4" />
                <p className="font-['Sora'] text-3xl font-bold text-white mb-2">
                  {stat.value}
                </p>
                <p className="font-['Roboto_Mono'] text-xs text-gray-400 uppercase tracking-wider">
                  {stat.label}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Testimonials */}
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ y: -10 }}
                className="relative group"
              >
                <div
                  className="bg-[#0B0E14]/90 backdrop-blur-xl border rounded-2xl p-8 h-full transition-all duration-300"
                  style={{
                    borderColor: `${testimonial.color}40`,
                    boxShadow: `0 0 30px ${testimonial.color}15, inset 0 0 50px ${testimonial.color}05`,
                  }}
                >
                  {/* Quote Icon */}
                  <Quote
                    className="w-10 h-10 mb-6 opacity-20"
                    style={{ color: testimonial.color }}
                  />

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 fill-current"
                        style={{ color: testimonial.color }}
                      />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="font-['Inter'] text-gray-300 leading-relaxed mb-6">
                    "{testimonial.text}"
                  </p>

                  {/* Metric Highlight */}
                  <div
                    className="mb-6 p-4 rounded-xl"
                    style={{
                      backgroundColor: `${testimonial.color}15`,
                      border: `1px solid ${testimonial.color}30`,
                    }}
                  >
                    <p
                      className="font-['Sora'] text-2xl font-bold text-center"
                      style={{ color: testimonial.color }}
                    >
                      {testimonial.metric}
                    </p>
                  </div>

                  {/* Author */}
                  <div className="flex items-center gap-4 pt-6 border-t border-gray-700">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center font-['Sora'] font-bold text-[#0B0E14]"
                      style={{
                        background: `linear-gradient(135deg, ${testimonial.color}, ${testimonial.color}CC)`,
                      }}
                    >
                      {testimonial.avatar}
                    </div>
                    <div>
                      <h4 className="font-['Sora'] text-white font-bold">
                        {testimonial.name}
                      </h4>
                      <p className="font-['Inter'] text-sm text-gray-400">
                        {testimonial.role}
                      </p>
                      <p className="font-['Roboto_Mono'] text-xs text-gray-500">
                        {testimonial.company}
                      </p>
                    </div>
                  </div>

                  {/* Hover Glow */}
                  <motion.div
                    className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{
                      background: `radial-gradient(circle at center, ${testimonial.color}20, transparent 70%)`,
                    }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Trust Banner */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="mt-16 bg-gradient-to-r from-[#10B981]/20 to-[#00F5FF]/20 border-2 border-[#10B981]/40 rounded-3xl p-10 text-center"
            style={{
              boxShadow: "0 0 60px rgba(16,185,129,0.2), inset 0 0 80px rgba(16,185,129,0.05)",
            }}
          >
            <h3 className="font-['Sora'] text-2xl font-bold text-white mb-4">
              Validado por desenvolvedores e aprovado por growth hackers
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-8 mt-8">
              {[
                "WordPress.org Verified",
                "WooCommerce Partner",
                "ISO 27001 Certified",
                "GDPR Compliant",
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="flex items-center gap-2 px-6 py-3 bg-[#0B0E14]/60 border border-[#10B981]/30 rounded-xl"
                >
                  <div className="w-2 h-2 bg-[#10B981] rounded-full" />
                  <span className="font-['Roboto_Mono'] text-sm text-gray-300">
                    {badge}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Final Social Proof Statement */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-center mt-12"
          >
            <p className="font-['Inter'] text-lg text-gray-400">
              Junte-se a centenas de lojas que já transformaram dados em receita
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
